import React, { useCallback, useMemo, useState } from "react";
import { joinClassNames } from "../utils/classNames";

export function GroupedHeader({ block, onClick, actions }) {
    const content = (
        <>
            <span className="grid-core-grouped-header-title">{block.label}</span>
            {block.subtitle && <span className="grid-core-grouped-header-subtitle">{block.subtitle}</span>}
        </>
    );

    if (actions) {
        const mainContent = onClick ? (
            <button
                type="button"
                className="grid-core-grouped-header-main grid-core-block-header-select-button"
                title={`选择 ${block.label} 列块`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onClick(block, event);
                }}
            >
                {content}
            </button>
        ) : (
            <div className="grid-core-grouped-header-main">
                {content}
            </div>
        );

        return (
            <div className="grid-core-grouped-header grid-core-grouped-header-with-actions">
                {mainContent}
                <div
                    className="grid-core-grouped-header-actions"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    {actions}
                </div>
            </div>
        );
    }

    if (onClick) {
        return (
            <button
                type="button"
                className="grid-core-grouped-header grid-core-block-header-select-button"
                title={`选择 ${block.label} 列块`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onClick(block, event);
                }}
            >
                {content}
            </button>
        );
    }

    return (
        <div className="grid-core-grouped-header">
            {content}
        </div>
    );
}

function normalizeFieldColumn(block, field, renderCell) {
    const columnKey = `${block.transactionType}.${field.key}`;
    return {
        ...field,
        key: columnKey,
        name: field.label || field.key,
        width: field.width,
        minWidth: field.minWidth || 72,
        editable: field.editable === true,
        resizable: field.resizable !== false,
        sortable: false,
        blockId: block.blockId,
        transactionType: block.transactionType,
        fieldKey: field.key,
        signalAware: field.signalAware === true,
        headerCellClass: joinClassNames("grid-core-field-header-cell", field.headerCellClass),
        cellClass: (row) => joinClassNames("grid-core-field-cell", typeof field.cellClass === "function" ? field.cellClass(row, block, field, columnKey) : field.cellClass),
        renderCell: (props) => renderCell?.(props, { block, field, columnKey }),
    };
}

export function flattenColumnBlocks({ frozenColumns = [], blocks = [], renderCell, onBlockHeaderClick, renderBlockHeaderActions } = {}) {
    const normalizedFrozenColumns = frozenColumns.map((column) => ({
        ...column,
        frozen: column.frozen !== false,
        sortable: false,
        resizable: column.resizable !== false,
        headerCellClass: joinClassNames("grid-core-frozen-header-cell", column.headerCellClass),
        cellClass: (row) => joinClassNames("grid-core-frozen-cell", typeof column.cellClass === "function" ? column.cellClass(row) : column.cellClass),
    }));

    const groupedColumns = blocks
        .filter((block) => block.visible !== false)
        .sort((left, right) => (left.order || 0) - (right.order || 0))
        .map((block) => {
            const headerActions = block.headerActions ?? renderBlockHeaderActions?.(block);
            return {
                name: <GroupedHeader block={block} onClick={onBlockHeaderClick} actions={headerActions} />,
                headerCellClass: joinClassNames("grid-core-block-header-cell", block.headerCellClass),
                children: (block.columns || []).map((field) => normalizeFieldColumn(block, field, renderCell)),
            };
        });

    return [...normalizedFrozenColumns, ...groupedColumns];
}

export function useColumnBlocks({ frozenColumns = [], columnBlocks = [], renderCell, onBlockHeaderClick, renderBlockHeaderActions } = {}) {
    const [visibilityByBlockId, setVisibilityByBlockId] = useState({});

    const visibleBlocks = useMemo(
        () =>
            columnBlocks
                .map((block) => ({
                    ...block,
                    visible: visibilityByBlockId[block.blockId] ?? block.visible !== false,
                }))
                .filter((block) => block.visible)
                .sort((left, right) => (left.order || 0) - (right.order || 0)),
        [columnBlocks, visibilityByBlockId],
    );

    const setBlockVisible = useCallback((blockId, visible) => {
        setVisibilityByBlockId((current) => ({
            ...current,
            [blockId]: visible,
        }));
    }, []);

    const resetBlockVisibility = useCallback(() => {
        setVisibilityByBlockId({});
    }, []);

    const columns = useMemo(
        () => flattenColumnBlocks({ frozenColumns, blocks: visibleBlocks, renderCell, onBlockHeaderClick, renderBlockHeaderActions }),
        [frozenColumns, onBlockHeaderClick, renderBlockHeaderActions, renderCell, visibleBlocks],
    );

    return {
        columns,
        visibleBlocks,
        setBlockVisible,
        resetBlockVisibility,
    };
}
